import React, { useEffect } from "react";

import { useState, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { X, Upload, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { generateImageBlurhash } from "@/lib/imgUtils";
import { storage } from "@/firebaseConfig";
import { cn } from "@/lib/utils";
import { ref, uploadBytesResumable } from "firebase/storage";

export interface UploadedImage {
  id: string;
  file: File;
  preview: string;
  status:
    | "pending"
    | "validating"
    | "uploading"
    | "generating-blurhash"
    | "complete"
    | "error";
  progress: number;
  url?: string;
  blurhash?: string;
  metadata?: {
    size: number;
    type?: string;
    encoding?: string;
    path: string;
  };
  error?: string;
}

export interface CompletedUpload {
  id: string;
  fileName: string;
  url: string;
  blurhash: string;
  metadata: {
    size: number;
    type?: string;
    encoding?: string;
    path: string;
  };
}

interface MultiPhotoUploadProps {
  getPath: (file: File) => string;
  onUploadsComplete?: (uploads: CompletedUpload[]) => boolean;
  maxFiles?: number;
  autoUpload?: boolean;
}

async function validateSquareImage(
  file: File,
): Promise<{ valid: boolean; error?: string }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      if (img.width !== img.height) {
        resolve({
          valid: false,
          error: `Image must be square. Got ${img.width}x${img.height}`,
        });
      } else {
        resolve({ valid: true });
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      resolve({ valid: false, error: "Failed to load image" });
    };
    img.src = URL.createObjectURL(file);
  });
}

async function uploadImage(
  image: UploadedImage,
  getPath: MultiPhotoUploadProps["getPath"],
  updateImage: (id: string, updates: Partial<UploadedImage>) => UploadedImage,
): Promise<UploadedImage> {
  return new Promise((resolve) => {
    const photosRef = ref(storage, getPath(image.file));

    const uploadTask = uploadBytesResumable(photosRef, image.file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        updateImage(image.id, { status: "uploading", progress });
      },
      (error) => {
        updateImage(image.id, {
          status: "error",
          error: error.message,
          progress: 0,
        });
        console.error(error);
      },
      async () => {
        const snapshot = uploadTask.snapshot;
        const url = `https://cdn.musicofourdesire.com/${snapshot.metadata.fullPath}`;
        const updatedImg = updateImage(image.id, {
          url,
          metadata: {
            size: snapshot.metadata.size,
            type: snapshot.metadata.contentType,
            encoding: snapshot.metadata.contentEncoding,
            path: snapshot.metadata.fullPath,
          },
          status: "complete",
        });
        resolve(updatedImg);
      },
    );
  });
}

export function MultiPhotoUpload({
  getPath,
  onUploadsComplete,
  maxFiles = 10,
  autoUpload = true,
}: MultiPhotoUploadProps) {
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [completedUploads, setCompletedUploads] = useState<CompletedUpload[]>(
    [],
  );
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const updateImage = useCallback(
    (id: string, updates: Partial<UploadedImage>) => {
      const image = images.find((i) => i.id === id)!;
      const updatedImg = { ...image, ...updates };
      setImages((prev) =>
        prev.map((img) => (img.id === id ? updatedImg : img)),
      );
      return updatedImg;
    },
    [images],
  );

  const addCompletedUpload = useCallback((upload: CompletedUpload) => {
    setCompletedUploads((prev) => {
      const newUploads = [...prev, upload];
      return newUploads;
    });
  }, []);

  const handleFiles = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      const webpFiles = fileArray.filter((f) => f.type === "image/webp");

      if (webpFiles.length !== fileArray.length) {
        alert("Only WebP images are allowed. Some files were skipped.");
      }

      if (images.length + webpFiles.length > maxFiles) {
        alert(`Maximum ${maxFiles} files allowed.`);
        return;
      }

      const newImages: UploadedImage[] = webpFiles.map((file) => ({
        id: crypto.randomUUID(),
        file,
        preview: URL.createObjectURL(file),
        status: "pending",
        progress: 0,
      }));

      setImages((prev) => [...prev, ...newImages]);
    },
    [images.length, maxFiles],
  );

  const removeImage = useCallback((id: string) => {
    setImages((prev) => {
      const img = prev.find((i) => i.id === id);
      if (img) {
        URL.revokeObjectURL(img.preview);
      }
      return prev.filter((i) => i.id !== id);
    });
  }, []);

  const processImage = async (image: UploadedImage) => {
    try {
      // Validate square
      updateImage(image.id, { status: "validating" });

      const validation = await validateSquareImage(image.file);
      if (!validation.valid) {
        updateImage(image.id, { status: "error", error: validation.error });
        return null;
      }

      // Get resumable upload URL
      updateImage(image.id, { status: "uploading", progress: 0 });

      const updatedImg = await uploadImage(image, getPath, updateImage);
      // Generate blurhash
      updateImage(image.id, { status: "generating-blurhash", progress: 100 });
      const blurhash = await generateImageBlurhash(
        URL.createObjectURL(image.file),
      );

      updateImage(image.id, { status: "complete", progress: 100 });

      const completedUpload: CompletedUpload = {
        id: image.id,
        fileName: image.file.name,
        url: updatedImg.url!,
        metadata: updatedImg.metadata!,
        blurhash,
      };

      addCompletedUpload(completedUpload);
      return completedUpload;
    } catch (error) {
      updateImage(image.id, {
        status: "error",
        error: error instanceof Error ? error.message : "Upload failed",
      });
      return null;
    }
  };

  const startUpload = async () => {
    if (images.length === 0) return;

    setIsUploading(true);
    setCompletedUploads([]);

    const pendingImages = images.filter(
      (img) => img.status === "pending" || img.status === "error",
    );

    // Process all images in parallel
    const results = await Promise.all(pendingImages.map(processImage));
    const successful = results.filter((r): r is CompletedUpload => r !== null);

    if (onUploadsComplete && successful.length > 0) {
      const finished = onUploadsComplete(successful);
      if (finished) {
        setImages([]);
        setCompletedUploads([]);
      }
    }

    setIsUploading(false);
  };

  useEffect(() => {
    if (!autoUpload || isUploading) return;
    if (images.some((img) => img.status === "pending")) {
      startUpload();
    }
  }, [images]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const getStatusIcon = (status: UploadedImage["status"]) => {
    switch (status) {
      case "validating":
      case "uploading":
      case "generating-blurhash":
        return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
      case "complete":
        return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
      case "error":
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusText = (status: UploadedImage["status"]) => {
    switch (status) {
      case "validating":
        return "Validating...";
      case "uploading":
        return "Uploading...";
      case "generating-blurhash":
        return "Generating blurhash...";
      case "complete":
        return "Complete";
      case "error":
        return "Error";
      default:
        return "Pending";
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto space-y-6">
      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={cn(
          "relative border-2 border-dashed rounded-lg p-8 transition-colors cursor-pointer",
          "hover:border-primary/50 hover:bg-muted/50",
          isDragging
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/25",
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/webp"
          multiple
          onChange={(e) => e.target.files && handleFiles(e.target.files)}
          className="hidden"
        />
        <div className="flex flex-col items-center justify-center gap-3 text-center">
          <div className="p-3 rounded-full bg-muted">
            <Upload className="h-6 w-6 text-muted-foreground" />
          </div>
          <div>
            <p className="font-medium">
              Drop WebP images here or click to browse
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Only square WebP images are allowed (max {maxFiles} files)
            </p>
          </div>
        </div>
      </div>

      {/* Image Preview Grid */}
      {images.length > 0 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {images.map((image) => (
              <Card
                key={image.file.name}
                className="relative overflow-hidden group"
              >
                <CardContent className="p-2">
                  <div className="relative aspect-square rounded-md overflow-hidden bg-muted">
                    <img
                      src={image.preview || "/placeholder.svg"}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                    {image.status !== "pending" && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <div className="text-center text-white space-y-1">
                          {getStatusIcon(image.status)}
                          <p className="text-xs">
                            {getStatusText(image.status)}
                          </p>
                          {image.status === "uploading" && (
                            <p className="text-xs font-medium">
                              {image.progress}%
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    {!isUploading && (
                      <button
                        onClick={() => removeImage(image.id)}
                        className="absolute top-1 right-1 p-1 rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  {image.status === "uploading" && (
                    <Progress value={image.progress} className="mt-2 h-1" />
                  )}
                  {image.error && (
                    <p
                      className="text-xs text-red-500 mt-1 truncate"
                      title={image.error}
                    >
                      {image.error}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              {images.length} image{images.length !== 1 ? "s" : ""} selected
            </p>
            {!autoUpload && (
              <Button
                onClick={startUpload}
                disabled={
                  isUploading ||
                  images.every((img) => img.status === "complete")
                }
              >
                {isUploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload All
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* {completedUploads.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              <h3 className="font-semibold">
                Completed Uploads ({completedUploads.length})
              </h3>
            </div>
            <div className="flex flex-row space-x-2">
              {completedUploads.map((upload) => (
                <div key={upload.id} className="max-h-60 overflow-auto">
                  <PhotoWithBlurhash
                    key={upload.id}
                    url={upload.url}
                    blurhash={upload.blurhash}
                    parentClassName="w-48 h-48"
                  />
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t">
              <p className="text-xs text-muted-foreground mb-2">
                Result Array (JSON):
              </p>
              <pre className="text-xs bg-muted p-2 rounded overflow-auto max-h-40">
                {JSON.stringify(completedUploads, null, 2)}
              </pre>
            </div> 
          </CardContent>
        </Card>
      )} */}
    </div>
  );
}
